import { api } from './api-client';

// ============================================================================
// Types
// ============================================================================

export interface ScanUploadResponse {
  scanId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  worksheetId: string;
  worksheetTitle: string;
  sheetNumber?: string;
  message: string;
}

export interface ExtractionResult {
  extractedValue: string | string[];
  confidence: number;
  tokens: number;
  error?: string;
}

export interface GradingResult {
  questionId: string;
  questionType: string;
  extractedAnswer: string | string[];
  expectedAnswer: string | string[];
  isCorrect: boolean;
  score: number;
  maxScore: number;
  feedback?: string;
  teacherOverride?: boolean;
}

export interface ScanResult {
  scanId: string;
  userId: string;
  worksheetId: string;
  worksheetTitle: string;
  sheetNumber?: string;
  imagePath: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  extractionResults?: Record<string, ExtractionResult>;
  gradingResults?: GradingResult[];
  totalScore?: number;
  maxScore?: number;
  percentage?: number;
  requiresReview?: boolean;
  reviewedByTeacher?: boolean;
  createdAt: string;
  updatedAt: string;
  error?: string;
}

export interface ScanListItem {
  scanId: string;
  worksheetId: string;
  worksheetTitle: string;
  sheetNumber?: string;
  status: string;
  totalScore?: number;
  maxScore?: number;
  percentage?: number;
  requiresReview: boolean;
  reviewedByTeacher: boolean;
  createdAt: string;
}

export interface CorrectionItem {
  questionId: string;
  correct?: boolean;
  score?: number;
  feedback?: string;
}

interface ScanListResponse {
  items: ScanListItem[];
  total: number;
}

// ============================================================================
// Helper: Transform snake_case to camelCase
// ============================================================================

function transformScanResponse(data: Record<string, unknown>): ScanResult {
  return {
    scanId: data.scan_id as string || data.scanId as string,
    userId: data.user_id as string || data.userId as string,
    worksheetId: data.worksheet_id as string || data.worksheetId as string,
    worksheetTitle: data.worksheet_title as string || data.worksheetTitle as string,
    sheetNumber: data.sheet_number as string || data.sheetNumber as string,
    imagePath: data.image_path as string || data.imagePath as string,
    status: (data.status as ScanResult['status']) || 'pending',
    extractionResults: data.extraction_results as Record<string, ExtractionResult> || data.extractionResults as Record<string, ExtractionResult>,
    gradingResults: transformGradingResults(data.grading_results || data.gradingResults),
    totalScore: data.total_score as number || data.totalScore as number,
    maxScore: data.max_score as number || data.maxScore as number,
    percentage: data.percentage as number,
    requiresReview: data.requires_review as boolean ?? data.requiresReview as boolean ?? false,
    reviewedByTeacher: data.reviewed_by_teacher as boolean ?? data.reviewedByTeacher as boolean ?? false,
    createdAt: data.created_at as string || data.createdAt as string,
    updatedAt: data.updated_at as string || data.updatedAt as string,
    error: data.error as string,
  };
}

function transformGradingResults(results: unknown): GradingResult[] | undefined {
  if (!results || !Array.isArray(results)) return undefined;

  return results.map((r: Record<string, unknown>) => ({
    questionId: r.question_id as string || r.questionId as string,
    questionType: r.question_type as string || r.questionType as string,
    extractedAnswer: r.extracted_answer as string | string[] || r.extractedAnswer as string | string[],
    expectedAnswer: r.expected_answer as string | string[] || r.expectedAnswer as string | string[],
    isCorrect: r.is_correct as boolean ?? r.isCorrect as boolean ?? false,
    score: r.score as number || 0,
    maxScore: r.max_score as number || r.maxScore as number || 1,
    feedback: r.feedback as string,
    teacherOverride: r.teacher_override as boolean ?? r.teacherOverride as boolean,
  }));
}

function transformScanListItem(data: Record<string, unknown>): ScanListItem {
  return {
    scanId: data.scan_id as string || data.scanId as string,
    worksheetId: data.worksheet_id as string || data.worksheetId as string,
    worksheetTitle: data.worksheet_title as string || data.worksheetTitle as string,
    sheetNumber: data.sheet_number as string || data.sheetNumber as string,
    status: data.status as string || 'pending',
    totalScore: data.total_score as number || data.totalScore as number,
    maxScore: data.max_score as number || data.maxScore as number,
    percentage: data.percentage as number,
    requiresReview: data.requires_review as boolean ?? data.requiresReview as boolean ?? false,
    reviewedByTeacher: data.reviewed_by_teacher as boolean ?? data.reviewedByTeacher as boolean ?? false,
    createdAt: data.created_at as string || data.createdAt as string,
  };
}

// ============================================================================
// Service
// ============================================================================

class ScanService {
  /**
   * Upload a new scan image.
   * Requires authentication and credits.
   */
  async uploadScan(
    file: File,
    worksheetId: string,
    sheetNumber?: string,
    classId?: string,
    signal?: AbortSignal
  ): Promise<ScanUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('worksheet_id', worksheetId);
    if (sheetNumber) {
      formData.append('sheet_number', sheetNumber);
    }
    if (classId) {
      formData.append('class_id', classId);
    }

    const response = await api.post<Record<string, unknown>>('/scans', formData, {
      signal,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      scanId: response.scan_id as string,
      status: response.status as ScanUploadResponse['status'],
      worksheetId: response.worksheet_id as string,
      worksheetTitle: response.worksheet_title as string,
      sheetNumber: response.sheet_number as string,
      message: response.message as string,
    };
  }

  /**
   * Trigger extraction and grading for a scan.
   * This processes the uploaded image and returns results.
   */
  async extractScan(scanId: string, signal?: AbortSignal): Promise<ScanResult> {
    const response = await api.post<Record<string, unknown>>(
      `/scans/${scanId}/extract`,
      undefined,
      { signal }
    );
    return transformScanResponse(response);
  }

  /**
   * Get scan details and results.
   */
  async getScan(scanId: string, signal?: AbortSignal): Promise<ScanResult> {
    const response = await api.get<Record<string, unknown>>(`/scans/${scanId}`, { signal });
    return transformScanResponse(response);
  }

  /**
   * List scans for the current user.
   */
  async listScans(
    params?: { worksheetId?: string; status?: string; limit?: number },
    signal?: AbortSignal
  ): Promise<ScanListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.worksheetId) {
      searchParams.append('worksheet_id', params.worksheetId);
    }
    if (params?.status) {
      searchParams.append('status', params.status);
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/scans?${queryString}` : '/scans';

    const response = await api.get<{ items: Record<string, unknown>[]; total: number }>(url, { signal });

    return {
      items: response.items.map(transformScanListItem),
      total: response.total,
    };
  }

  /**
   * Apply teacher corrections to grading results.
   */
  async applyCorrections(
    scanId: string,
    corrections: CorrectionItem[],
    signal?: AbortSignal
  ): Promise<ScanResult> {
    // Transform to snake_case for API
    const payload = {
      corrections: corrections.map(c => ({
        question_id: c.questionId,
        correct: c.correct,
        score: c.score,
        feedback: c.feedback,
      })),
    };

    const response = await api.put<Record<string, unknown>>(
      `/scans/${scanId}/corrections`,
      payload,
      { signal }
    );
    return transformScanResponse(response);
  }

  /**
   * Get the URL for a scan's image.
   * Note: This endpoint may need to be implemented on the backend.
   */
  getScanImageUrl(scanId: string): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8082';
    return `${baseUrl}/scans/${scanId}/image`;
  }
}

export const scanService = new ScanService();
